import ApplicationController, { observe } from '@aha-app/mvc';
import type {
  ISidePanelController,
  SidePanelItem,
  SidePanelProps,
} from '../../../shared-types';

interface SidePanelState {
  sidePanelOpen: boolean;
  sidePanelStack: SidePanelItem[];
  slideDepth: number;
  backButtonVisible: boolean;
  activeMenuItemIndex: number;
  disableSlideAnimation: boolean;
  hidden: boolean;
}

class SidePanelController
  extends ApplicationController<SidePanelState, SidePanelProps>
  implements ISidePanelController
{
  slideTimeout: ReturnType<typeof setTimeout> | null;
  stateObserver: ReturnType<typeof observe>;

  static initialState: SidePanelState = {
    sidePanelOpen: false,
    // Active sub-menus of the active side panel. Starts at depth 3, because
    // the menu is 1 and the top-level item is 2.
    sidePanelStack: [],
    slideDepth: 1,
    activeMenuItemIndex: -1,
    backButtonVisible: false,
    disableSlideAnimation: false,
    hidden: false,
  };

  async initialize(
    // eslint-disable-next-line no-empty-pattern
    { hidden }: SidePanelProps
  ): Promise<void> {
    this.slideTimeout = null;
    this.state.hidden = hidden || false;

    // Trigger state changes
    this.stateObserver = observe(() => {
      const { sidePanelOpen } = this.state;

      if (this.props.onChange) {
        this.props.onChange({ visible: sidePanelOpen });
      }
    });
  }

  destroy(): void {
    this.stateObserver();
  }

  changeProps(newProps: SidePanelProps): void {
    if (newProps.hidden !== undefined) {
      this.state.hidden = newProps.hidden;
    }
  }

  actionSetHidden(hidden: boolean): void {
    this.state.hidden = hidden;
  }

  actionToggleOpenState(open: boolean) {
    if (!open && this.state.sidePanelOpen) {
      this.activeMenuItem()?.onClose?.();
    }
    this.state.sidePanelOpen = open;

    // If there's only one item, open it by default
    if (open) {
      if (this.props.sidePanelItems.length === 1) {
        this.actionLoadMenuItem(0);
      }
      this.activeMenuItem()?.onOpen?.(); // Call onOpen for the currently active item
    }
  }

  activeMenuItem(): SidePanelItem | null {
    const item = this.props.sidePanelItems[this.state.activeMenuItemIndex];
    return item || null;
  }

  actionLoadMenuItemById(id?: string, options?: Record<string, any>): void {
    this.state.disableSlideAnimation = true;
    const index = this.props.sidePanelItems.findIndex(item => item.id === id);
    if (index !== -1) {
      this.actionLoadMenuItem(index, options);
    }
    setTimeout(() => {
      this.state.disableSlideAnimation = false;
    }, 300);
  }

  actionLoadMenuItem(index: number, options?: Record<string, any>): void {
    const isOpen =
      this.state.sidePanelOpen && this.state.activeMenuItemIndex === index;
    if (!isOpen) {
      this.state.activeMenuItemIndex = index;
      this.actionSlideToDepth(2);
      const tabName = this.props.sidePanelItems[index].name;
      if (tabName) {
        window.AhaAnalytics?.track(
          'Feature-7249764725472497103',
          `User opens ${tabName.toLowerCase()} tab`,
          {
            'Document type': this.props.documentType,
          }
        );
      }
    }

    // Even if the panel item was already "open", we still need to trigger onOpen
    // because the panel itself may have been closed in the interim
    this.activeMenuItem()?.onOpen?.(options);
  }

  actionMenuBack(): void {
    this.actionPopSidePanel();
  }

  actionSlideToDepth(depth: number): void {
    const allowBackButton = this.props.sidePanelItems.length > 1;
    if (this.slideTimeout !== null) {
      clearTimeout(this.slideTimeout);
    }
    this.state.backButtonVisible = allowBackButton;
    this.state.slideDepth = depth;
    this.slideTimeout = setTimeout(() => {
      this.slideTimeout = null;
      this.state.backButtonVisible = allowBackButton && depth > 1;
      if (depth === 1) {
        this.state.activeMenuItemIndex = -1;
      }
      if (
        this.state.sidePanelStack.length >
        Math.max(this.state.slideDepth - 2, 0)
      ) {
        this.state.sidePanelStack.length = this.state.slideDepth - 2;
      }
    }, 300);
  }

  actionOpenSidePanel(activeItemId?: string, options?: Record<string, any>) {
    this.actionToggleOpenState(true);
    this.actionLoadMenuItemById(activeItemId, options);
  }

  actionCloseSidePanel() {
    this.actionToggleOpenState(false);
  }

  actionPushSidePanel(item: SidePanelItem) {
    this.state.slideDepth += 1;

    this.state.sidePanelStack.push(item);
  }

  actionReplaceSidePanelStack(items: SidePanelItem[]) {
    this.state.slideDepth = items.length + 2;
    this.state.sidePanelStack = items;
  }

  actionPopSidePanel() {
    if (this.state.slideDepth <= 1) return;

    if (this.state.slideDepth === 2) this.activeMenuItem()?.onClose?.();
    this.actionSlideToDepth(this.state.slideDepth - 1);
  }

  get backButtonVisible(): boolean {
    return this.state.backButtonVisible || this.state.slideDepth >= 3;
  }

  get backButtonLabel(): string {
    if (this.state.slideDepth === 3) {
      const item = this.props.sidePanelItems[this.state.activeMenuItemIndex];
      if (item) {
        return item.name;
      }
    } else if (this.state.slideDepth > 3) {
      const item = this.state.sidePanelStack[this.state.slideDepth - 4];
      if (item) {
        return item.name;
      }
    }

    return 'Menu';
  }
}

export default SidePanelController;
